import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Clock, CheckIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  format,
  parse,
  setHours,
  startOfHour,
  endOfHour,
  setMinutes,
  startOfMinute,
  endOfMinute,
  setSeconds,
  startOfDay,
  endOfDay,
  addHours,
  subHours,
  setMilliseconds,
  isValid,
} from 'date-fns';
import { ScrollArea } from './ui/scroll-area';
import { FormControl, FormItem, FormLabel } from './ui/form';
import { useController, useFormContext } from 'react-hook-form';

interface SimpleTimeOption {
  value: number;
  label: string;
  disabled?: boolean;
}
type TimeFieldOptions = 'H' | 'M' | 'S';

const AM_VALUE = 0;
const PM_VALUE = 1;

export function FormFieldTimePicker({
  use12HourFormat,
  min,
  max,
  disabled,
  timeFields = ['H', 'M', 'S'],
  label,
  name,
  required,
  placeHolder = 'Select Time',
  disabledStyle,
  returnFormattedValue = false,
}: {
  use12HourFormat?: boolean;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  className?: string;
  timeFields?: TimeFieldOptions[];
  label: string;
  name: string;
  required?: boolean;
  placeHolder?: string;
  disabledStyle?: string;
  returnFormattedValue?: boolean;
}) {
  const { control, trigger } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  const handleFieldBlur = useCallback(() => {
    field.onBlur();
    void trigger(name);
  }, [field, name, trigger]);
  // Callers commonly pass timeFields as an inline array literal (e.g.
  // `timeFields={['H', 'M']}`), which is a new reference on every render.
  // Depend on its content instead of the array itself so the effects below
  // don't re-run (and briefly re-render this field) on every unrelated
  // parent re-render — only when the set of fields actually changes.
  const timeFieldsKey = useMemo(() => (timeFields ?? []).join(','), [timeFields]);
  const [value, setValue] = useState<Date | null>(() => {
    if (!field.value) return null;
    if (typeof field.value === 'string' && returnFormattedValue) {
      const fmt = timeFields?.includes('S')
        ? use12HourFormat
          ? 'hh:mm:ss a'
          : 'HH:mm:ss'
        : use12HourFormat
          ? 'hh:mm a'
          : 'HH:mm';
      try {
        const parsed = parse(field.value, fmt, new Date());
        return isValid(parsed) ? parsed : null;
      } catch (e) {
        console.error('Failed to parse time string', field.value);
        return null;
      }
    }
    const val = field.value instanceof Date ? field.value : new Date(field.value);
    return isValid(val) ? val : null;
  });

  useEffect(() => {
    if (field.value) {
      let val: Date | null = null;
      if (returnFormattedValue && typeof field.value === 'string') {
        const fmt = timeFields?.includes('S')
          ? use12HourFormat
            ? 'hh:mm:ss a'
            : 'HH:mm:ss'
          : use12HourFormat
            ? 'hh:mm a'
            : 'HH:mm';
        try {
          const parsed = parse(field.value, fmt, value ?? new Date());
          if (isValid(parsed)) val = parsed;
        } catch (e) {
          console.error('Failed to parse time string', field.value);
        }
      } else {
        const d = field.value instanceof Date ? field.value : new Date(field.value);
        if (isValid(d)) val = d;
      }

      if (val) {
        const newAmpm = format(val, 'a') === 'AM' ? AM_VALUE : PM_VALUE;
        const newHour = use12HourFormat ? +format(val, 'hh') : val.getHours();
        const newMinute = val.getMinutes();
        const newSecond = val.getSeconds();

        // Bail out if the parsed value already matches current internal state.
        // Without this, updating `value` here (a new Date instance every time)
        // feeds back into the effect below, which calls field.onChange again
        // and can re-run this effect — a feedback loop that showed up as the
        // AM/PM selector (and the hour/minute values) flickering/reverting.
        const isUnchanged =
          newAmpm === ampm &&
          newHour === hour &&
          newMinute === minute &&
          (!timeFields?.includes('S') || newSecond === second);
        if (isUnchanged) return;

        setValue(val);
        // Sync internal state
        setAmpm(newAmpm);
        setHour(newHour);
        setMinute(newMinute);
        setSecond(newSecond);
      }
    }
  }, [field.value, returnFormattedValue, use12HourFormat, timeFieldsKey]);

  const formatStr = useMemo(
    () => (use12HourFormat ? 'yyyy-MM-dd hh:mm:ss.SSS a xxxx' : 'yyyy-MM-dd HH:mm:ss.SSS xxxx'),
    [use12HourFormat]
  );
  const [ampm, setAmpm] = useState(value ? (format(value, 'a') === 'AM' ? AM_VALUE : PM_VALUE) : AM_VALUE);
  const [hour, setHour] = useState(value ? (use12HourFormat ? +format(value, 'hh') : value.getHours()) : null);
  const [minute, setMinute] = useState(value?.getMinutes() ?? null);
  const [second, setSecond] = useState(value?.getSeconds() ?? null);

  useEffect(() => {
    const time = buildTime({ use12HourFormat, value, formatStr, hour, minute, second, ampm });
    if (time) {
      // Skip the update if it wouldn't change anything. field.onChange is
      // otherwise called unconditionally, which can trigger a parent
      // re-render (e.g. via a form `watch`) that re-runs this effect again —
      // a feedback loop that showed up as the time/AM-PM value flickering.
      if (value && value.getTime() === time.getTime()) return;

      setValue(time);
      if (returnFormattedValue) {
        const fmt = timeFields?.includes('S')
          ? use12HourFormat
            ? 'hh:mm:ss a'
            : 'HH:mm:ss'
          : use12HourFormat
            ? 'hh:mm a'
            : 'HH:mm';
        field.onChange(format(time, fmt));
      } else {
        field.onChange(time);
      }
    }
  }, [hour, minute, second, ampm, formatStr, use12HourFormat, returnFormattedValue, timeFieldsKey]);

  const _hourIn24h = useMemo(() => {
    if (hour !== null && ampm !== null) return use12HourFormat ? (hour % 12) + ampm * 12 : hour;
  }, [hour, use12HourFormat, ampm]);

  const hours: SimpleTimeOption[] | undefined = useMemo(() => {
    return Array.from({ length: use12HourFormat ? 12 : 24 }, (_, i) => {
      let disabled = false;
      const hourValue = use12HourFormat ? (i === 0 ? 12 : i) : i;
      const hDate = setHours(value || new Date(), use12HourFormat ? i + (ampm || 0) * 12 : i);
      const hStart = startOfHour(hDate);
      const hEnd = endOfHour(hDate);
      if (min && hEnd < min) disabled = true;
      if (max && hStart > max) disabled = true;
      return {
        value: hourValue,
        label: hourValue.toString().padStart(2, '0'),
        disabled,
      };
    });
  }, [value, min, max, use12HourFormat, ampm]);

  const minutes: SimpleTimeOption[] | undefined = useMemo(() => {
    const anchorDate = setHours(value || new Date(), _hourIn24h || 0);
    return Array.from({ length: 60 }, (_, i) => {
      let disabled = false;
      const mDate = setMinutes(anchorDate, i);
      const mStart = startOfMinute(mDate);
      const mEnd = endOfMinute(mDate);
      if (min && mEnd < min) disabled = true;
      if (max && mStart > max) disabled = true;
      return {
        value: i,
        label: i.toString().padStart(2, '0'),
        disabled,
      };
    });
  }, [value, min, max, _hourIn24h]);

  const seconds: SimpleTimeOption[] | undefined = useMemo(() => {
    const anchorDate = setMilliseconds(setMinutes(setHours(value || new Date(), _hourIn24h || 0), minute || 0), 0);
    const _min = min ? setMilliseconds(min, 0) : undefined;
    const _max = max ? setMilliseconds(max, 0) : undefined;
    return Array.from({ length: 60 }, (_, i) => {
      let disabled = false;
      const sDate = setSeconds(anchorDate, i);
      if (_min && sDate < _min) disabled = true;
      if (_max && sDate > _max) disabled = true;
      return {
        value: i,
        label: i.toString().padStart(2, '0'),
        disabled,
      };
    });
  }, [value, minute, min, max, _hourIn24h]);

  const ampmOptions = useMemo(() => {
    const startD = startOfDay(value || new Date());
    const endD = endOfDay(value || new Date());
    return [
      { value: AM_VALUE, label: 'AM' },
      { value: PM_VALUE, label: 'PM' },
    ].map((v) => {
      let disabled = false;
      const start = addHours(startD, v.value * 12);
      const end = subHours(endD, (1 - v.value) * 12);
      if (min && end < min) disabled = true;
      if (max && start > max) disabled = true;
      return { ...v, disabled };
    });
  }, [value, min, max]);

  const [open, setOpen] = useState(false);

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const secondRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (open) {
        hourRef.current?.scrollIntoView({ behavior: 'auto' });
        minuteRef.current?.scrollIntoView({ behavior: 'auto' });
        secondRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    }, 1);
    return () => clearTimeout(timeoutId);
  }, [open]);

  const onHourChange = useCallback(
    (v: SimpleTimeOption) => {
      if (min) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour: v.value, minute, second, ampm });
        if (newTime && newTime < min) {
          setMinute(min.getMinutes());
          setSecond(min.getSeconds());
        }
      }
      if (max) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour: v.value, minute, second, ampm });
        if (newTime && newTime > max) {
          setMinute(max.getMinutes());
          setSecond(max.getSeconds());
        }
      }
      setHour(v.value);
    },
    [setHour, use12HourFormat, value, formatStr, minute, second, ampm]
  );

  const onMinuteChange = useCallback(
    (v: SimpleTimeOption) => {
      if (min) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour: v.value, minute, second, ampm });
        if (newTime && newTime < min) {
          setSecond(min.getSeconds());
        }
      }
      if (max) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour: v.value, minute, second, ampm });
        if (newTime && newTime > max) {
          setSecond(newTime.getSeconds());
        }
      }
      setMinute(v.value);
    },
    [setMinute, use12HourFormat, value, formatStr, hour, second, ampm]
  );

  const onAmpmChange = useCallback(
    (v: SimpleTimeOption) => {
      if (min) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour, minute, second, ampm: v.value });
        if (newTime && newTime < min) {
          const minH = min.getHours() % 12;
          setHour(minH === 0 ? 12 : minH);
          setMinute(min.getMinutes());
          setSecond(min.getSeconds());
        }
      }
      if (max) {
        const newTime = buildTime({ use12HourFormat, value, formatStr, hour, minute, second, ampm: v.value });
        if (newTime && newTime > max) {
          const maxH = max.getHours() % 12;
          setHour(maxH === 0 ? 12 : maxH);
          setMinute(max.getMinutes());
          setSecond(max.getSeconds());
        }
      }
      setAmpm(v.value);
    },
    [setAmpm, use12HourFormat, value, formatStr, hour, minute, second, min, max]
  );

  const display = useMemo(() => {
    if (!value) return placeHolder;
    if (timeFields.includes('S')) return format(value, use12HourFormat ? 'hh:mm:ss a' : 'HH:mm:ss');
    else return format(value, use12HourFormat ? 'hh:mm a' : 'HH:mm');
  }, [value, use12HourFormat]);
  return (
    <FormItem className={`flex flex-col gap-1`}>
      <FormLabel htmlFor={name} className="block text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
      </FormLabel>
      <FormControl className="flex items-center">
        <Popover
          open={disabled ? false : open}
          onOpenChange={(open) => {
            setOpen(open);
            if (!open) handleFieldBlur();
          }}
        >
          <PopoverTrigger asChild>
            <div
              role="combobox"
              aria-expanded={open}
              tabIndex={disabled ? -1 : 0}
              onBlur={() => {
                if (!open) handleFieldBlur();
              }}
              className={cn(
                'flex h-12 w-full rounded-md bg-white px-3 py-1 text-sm font-[400] border-[1px] border-bordercolor file:border-0 file:bg-transparent placeholder:text-placeholder placeholder:font-normal file:text-sm file:font-medium focus:border-[1px]  disabled:opacity-50 focus-visible:outline-none items-center disabled:cursor-not-allowed',
                !value && 'text-placeholder',
                (disabled && disabledStyle) ?? '',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer focus:border-focuscolor'
              )}
            >
              <Clock className="mr-2 size-4" />
              {display}
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0" align="start" side="bottom">
            <div className="flex-col gap-2 p-2">
              <div className="flex h-56 grow">
                <ScrollArea className="h-full flex-grow">
                  <div className="flex grow flex-col items-stretch overflow-y-auto pe-2 ">
                    {timeFields?.includes('H') &&
                      hours?.map((v) => (
                        <div ref={v.value === hour ? hourRef : undefined} key={v.value}>
                          <TimeItem
                            option={v}
                            selected={v.value === hour}
                            onSelect={onHourChange}
                            disabled={v.disabled}
                            className="h-8"
                          />
                        </div>
                      ))}
                  </div>
                </ScrollArea>
                <ScrollArea className="h-full flex-grow">
                  <div className="flex grow flex-col items-stretch overflow-y-auto pe-2 ">
                    {timeFields?.includes('M') &&
                      minutes?.map((v) => (
                        <div ref={v.value === minute ? minuteRef : undefined} key={v.value}>
                          <TimeItem
                            option={v}
                            selected={v.value === minute}
                            onSelect={onMinuteChange}
                            disabled={v.disabled}
                            className="h-8"
                          />
                        </div>
                      ))}
                  </div>
                </ScrollArea>
                <ScrollArea className="h-full flex-grow">
                  <div className="flex grow flex-col items-stretch overflow-y-auto pe-2 ">
                    {timeFields?.includes('S') &&
                      seconds?.map((v) => (
                        <div ref={v.value === second ? secondRef : undefined} key={v.value}>
                          <TimeItem
                            option={v}
                            selected={v.value === second}
                            onSelect={(v) => setSecond(v.value)}
                            className="h-8"
                            disabled={v.disabled}
                          />
                        </div>
                      ))}
                  </div>
                </ScrollArea>

                {use12HourFormat && (
                  <ScrollArea className="h-full flex-grow">
                    <div className="flex grow flex-col items-stretch overflow-y-auto pe-2">
                      {ampmOptions?.map((v) => (
                        <TimeItem
                          key={v.value}
                          option={v}
                          selected={v.value === ampm}
                          onSelect={onAmpmChange}
                          className="h-8"
                          disabled={v.disabled}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </FormControl>
      {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
    </FormItem>
  );
}

const TimeItem = ({
  option,
  selected,
  onSelect,
  className,
  disabled,
}: {
  option: SimpleTimeOption;
  selected: boolean;
  onSelect: (option: SimpleTimeOption) => void;
  className?: string;
  disabled?: boolean;
}) => {
  return (
    <Button
      variant="ghost"
      className={cn('flex justify-center px-1 pe-2 ps-1', className)}
      onClick={() => onSelect(option)}
      disabled={disabled}
    >
      <div className="w-4">{selected && <CheckIcon className="my-auto size-4" />}</div>
      <span className="ms-2">{option.label}</span>
    </Button>
  );
};

interface BuildTimeOptions {
  use12HourFormat?: boolean;
  value: Date | null;
  formatStr: string;
  hour: number | null;
  minute: number | null;
  second: number | null;
  ampm: number | null;
}

const buildTime = (options: BuildTimeOptions) => {
  const { use12HourFormat, value, formatStr, hour, minute, second, ampm } = options;
  let date: Date;
  const v = value ?? new Date();
  if (use12HourFormat) {
    if (v && hour !== null && minute !== null) {
      const dateStrRaw = format(v, formatStr);
      let dateStr = dateStrRaw.slice(0, 11) + hour.toString().padStart(2, '0') + dateStrRaw.slice(13);
      dateStr = dateStr.slice(0, 14) + minute.toString().padStart(2, '0') + dateStr.slice(16);
      dateStr = dateStr.slice(0, 17) + (second ?? 0).toString().padStart(2, '0') + dateStr.slice(19);
      dateStr = dateStr.slice(0, 24) + (ampm == AM_VALUE ? 'AM' : 'PM') + dateStr.slice(26);
      date = parse(dateStr, formatStr, v);
      return date;
    }
  } else {
    if (v && hour !== null && minute !== null) {
      date = setHours(setMinutes(setSeconds(setMilliseconds(v, 0), second ?? 0), minute), hour);
      return date;
    }
  }
};
